import React, { useState, useEffect, useRef } from 'react';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import API, { getImagenUrl } from 'shared/api';
import { FiPlus, FiStar, FiTrash2, FiSave, FiRefreshCw, FiDownload, FiCheck, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import SyncTab from 'components/Sincronizacion/SyncTab';
import TicketCanvas from 'components/Tienda/TicketCanvas';
import { FUENTES } from 'components/Tienda/ticketLayout';
import DispositivosVinculados from 'shared/components/DispositivosVinculados';

const API_URL = process.env.REACT_APP_API_URL || '';

const MP_ICONS = {
  efectivo_usd: '💵',
  efectivo_bs: '💵',
  efectivo_pesos: '💵',
  pago_movil: '📱',
  binance: '₿',
  paypal: '🅿️',
  nequi: '💰',
  zelle: '💸',
  transferencia: '🏦',
};

const PLANTILLAS = [
  {
    nombre: 'Azul Marino',
    primario: '#0B1A4A',
    secundario: '#2952CC',
    fondo: '#F5F7FA',
    exito: '#34a853',
    peligro: '#ea4335',
  },
  {
    nombre: 'Verde Bosque',
    primario: '#1B4332',
    secundario: '#40916C',
    fondo: '#F0F7F4',
    exito: '#2D6A4F',
    peligro: '#D00000',
  },
  {
    nombre: 'Negro Minimal',
    primario: '#111111',
    secundario: '#444444',
    fondo: '#FAFAFA',
    exito: '#2E7D32',
    peligro: '#DC2626',
  },
  {
    nombre: 'Purpura Royal',
    primario: '#4A148C',
    secundario: '#7C3AED',
    fondo: '#F8F6FF',
    exito: '#059669',
    peligro: '#E11D48',
  },
  {
    nombre: 'Naranja Calido',
    primario: '#E65100',
    secundario: '#F57C00',
    fondo: '#FFF8F0',
    exito: '#43A047',
    peligro: '#D32F2F',
  },
  {
    nombre: 'Gris Profesional',
    primario: '#37474F',
    secundario: '#607D8B',
    fondo: '#F5F6F8',
    exito: '#388E3C',
    peligro: '#D50000',
  },
  {
    nombre: 'Teal Moderno',
    primario: '#00695C',
    secundario: '#00897B',
    fondo: '#F0FAF8',
    exito: '#2E7D32',
    peligro: '#C62828',
  },
  {
    nombre: 'Indigo Oscuro',
    primario: '#283593',
    secundario: '#5C6BC0',
    fondo: '#F4F5FC',
    exito: '#2E7D32',
    peligro: '#E53935',
  },
  {
    nombre: 'Amarillo + Negro + Morado',
    primario: '#E4B02E',
    secundario: '#000000',
    fondo: '#F8F4FC',
    exito: '#A383C1',
    peligro: '#C0392B',
  },
];

const COLOR_LABELS = [
  { key: 'color_primario', label: 'Primario' },
  { key: 'color_secundario', label: 'Secundario' },
  { key: 'color_fondo', label: 'Fondo' },
  { key: 'color_exito', label: 'Exito' },
  { key: 'color_peligro', label: 'Peligro' },
];

const DELIVERY_OPTS = [
  { v: 'retiro', l: 'Retiro en tienda', d: 'El cliente recoge su pedido en la sede' },
  { v: 'delivery', l: 'Delivery', d: 'Se envia el pedido a la dirección del cliente' },
  { v: 'encomienda', l: 'Encomienda', d: 'Se despacha por servicio de encomienda externo' },
];

const MODO_OPTS = [
  {
    v: 'ambos',
    l: 'Catálogo + Chat',
    d: 'El cliente puede navegar productos y chatear con el bot',
  },
  {
    v: 'solo_catalogo',
    l: 'Solo Catálogo',
    d: 'Solo se muestra el catálogo de productos, sin chat',
  },
  { v: 'solo_chat', l: 'Solo Chat', d: 'Solo se muestra el chat con el bot de IA' },
];

const MODO_REGISTRO_OPTS = [
  {
    v: 'simple',
    l: 'Simple',
    d: 'El cliente se identifica automáticamente por su navegador. Sin usuario ni contraseña.',
  },
  {
    v: 'completo',
    l: 'Completo',
    d: 'El cliente debe registrarse con usuario y contraseña. Accede a su perfil e historial cross-device.',
  },
];

const TABS = [
  {
    id: 'perfil',
    label: 'Mi Perfil',
    icon: '👤',
    desc: 'Tu información personal y credenciales de acceso',
  },
  {
    id: 'apariencia',
    label: 'Apariencia',
    icon: '🎨',
    desc: 'Colores, logo y nombre de tu tienda',
    adminOnly: true,
  },
  {
    id: 'operacion',
    label: 'Operacion',
    icon: '🚚',
    desc: 'Métodos de entrega y modo de atención al cliente',
    adminOnly: true,
  },
  {
    id: 'monedas',
    label: 'Monedas',
    icon: '💱',
    desc: 'Monedas aceptadas y tasas de cambio',
    adminOnly: true,
  },
  {
    id: 'pagos',
    label: 'Pagos',
    icon: '💳',
    desc: 'Configura los métodos de pago por cada sede',
    adminOnly: true,
  },
  {
    id: 'tickets',
    label: 'Tickets',
    icon: '🧾',
    desc: 'Personaliza el ticket de entrega y el ticket de cobro',
    adminOnly: true,
  },
  {
    id: 'sincronizacion',
    label: 'Sincronización',
    icon: '🔄',
    desc: 'Conecta y sincroniza tu base de datos externa',
    adminOnly: true,
    soloExterno: true,
  },
  {
    id: 'migracion',
    label: 'Migrar desde .fdb',
    icon: '📥',
    desc: 'Migra tu base ADVIN (Firebird) a Agorax para operar en modo interno',
    adminOnly: true,
  },
];

const rolColor = {
  admin: '#ea4335',
  supervisor: '#fbbc04',
  asesor: '#1a73e8',
  delivery: '#34a853',
};
const RBAC_ROLE_COLOR = '#7c3aed';

const TAB_PERMISOS = {
  perfil: 'config.perfil',
  apariencia: 'config.apariencia',
  operacion: 'config.operacion',
  monedas: 'config.monedas',
  pagos: 'config.pagos',
  tickets: 'config.tickets',
  sincronizacion: 'config.sync',
  migracion: 'config.sync',
};

function Configuración({ onNavigate }) {
  const { usuario, getRolLabel, getSedeInfo, isPlanExterno, tienePermiso } = useAuth();
  const sedeInfo = getSedeInfo();

  const [tab, setTab] = useState('perfil');

  const tabsVisibles = TABS.filter((t) => {
    const permiso = TAB_PERMISOS[t.id];
    if (permiso && !tienePermiso(permiso)) return false;
    if (t.adminOnly && !tienePermiso(permiso)) return false;
    if (t.soloExterno && !isPlanExterno()) return false;
    return true;
  });

  return (
    <div className="cfg-wrapper" style={S.wrapper}>
      <style>{`
        @keyframes cfgSlideIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .cfg-tab-content { animation: cfgSlideIn 0.2s ease; }
        @media (max-width: 768px) {
          .cfg-wrapper { padding: 16px 12px !important; }
          .cfg-card { padding: 18px !important; }
          .cfg-pay-grid { grid-template-columns: 1fr !important; }
          .cfg-edit-fields { grid-template-columns: 1fr !important; }
          .cfg-tabs { gap: 2px !important; overflow-x: auto !important; padding-bottom: 4px !important; }
          .cfg-tab-btn { padding: 10px 12px !important; font-size: 11px !important; white-space: nowrap !important; }
          .cfg-monedas-grid { grid-template-columns: 1fr !important; }
          .tickets-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .tickets-preview-col { order: -1 !important; position: static !important; }
          .tickets-appear-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: 'var(--agx-text)',
            letterSpacing: '-0.4px',
          }}
        >
          Configuración
        </div>
        <div style={{ fontSize: '13px', color: 'var(--agx-faint)', marginTop: '4px' }}>
          Administra tu perfil y los ajustes de tu empresa
        </div>
      </div>

      {/* TAB NAVIGATION */}
      <div
        className="cfg-tabs"
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          borderBottom: '1.5px solid #eef0f4',
          paddingBottom: '0',
        }}
      >
        {tabsVisibles.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="cfg-tab-btn"
            style={{
              padding: '12px 18px',
              border: 'none',
              borderBottom: tab === t.id ? '2.5px solid #2563eb' : '2.5px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? '#2563eb' : '#6b7280',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s',
              marginBottom: '-1.5px',
            }}
          >
            <span style={{ fontSize: '15px' }}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* TAB DESCRIPTION */}
      {(() => {
        const activeTab = TABS.find((t) => t.id === tab);
        return activeTab ? (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--agx-faint)',
              marginBottom: '16px',
              marginTop: '-12px',
            }}
          >
            {activeTab.desc}
          </div>
        ) : null;
      })()}

      {/* TAB CONTENT */}
      <div className="cfg-tab-content">
        {tab === 'perfil' && (
          <TabPerfil
            usuario={usuario}
            getRolLabel={getRolLabel}
            sedeInfo={sedeInfo}
            rbacRoleColor={RBAC_ROLE_COLOR}
            rolColor={rolColor}
          />
        )}
        {tab === 'apariencia' && tienePermiso('config.apariencia') && (
          <TabApariencia usuario={usuario} API_URL={API_URL} />
        )}
        {tab === 'operacion' && tienePermiso('config.operacion') && <TabOperacion />}
        {tab === 'monedas' && tienePermiso('config.monedas') && <TabMonedas />}
        {tab === 'pagos' && tienePermiso('config.pagos') && (
          <TabPagos usuario={usuario} tienePermiso={tienePermiso} MP_ICONS={MP_ICONS} />
        )}
        {tab === 'tickets' && tienePermiso('config.tickets') && <TabTickets />}
        {tab === 'sincronizacion' && tienePermiso('config.sync') && isPlanExterno() && <SyncTab />}
        {tab === 'migracion' && tienePermiso('config.sync') && <TabMigracion />}
      </div>
    </div>
  );
}

/* ════════════════════ TAB: MIGRACION (.fdb) ════════════════════ */
function TabMigracion() {
  const [sedes, setSedes] = useState([]);
  const [sedeSel, setSedeSel] = useState('');
  const [codigo, setCodigo] = useState('');
  const [expira, setExpira] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    try {
      const [s, h] = await Promise.all([
        API.get('/migracion/sedes'),
        API.get('/migracion/historial'),
      ]);
      const lista = s?.data?.data || [];
      setSedes(lista);
      if (lista.length && !sedeSel) setSedeSel(lista[0].codigo);
      setHistorial(h?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la migracion');
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generar = async () => {
    setGenerando(true);
    setError('');
    setCodigo('');
    setExpira(null);
    try {
      const r = await API.post('/migracion/generar', { sede_codigo: sedeSel });
      setCodigo(r?.data?.data?.codigo_migracion || '');
      setExpira(r?.data?.data?.expira_horas || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo generar el codigo');
    } finally {
      setGenerando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigo);
    } catch (e) {
      // sin clipboard: seleccionar
    }
  };

  const descargar = async () => {
    setCargando(true);
    setError('');
    try {
      const res = await API.get('/migracion/descargar', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AgoraxMigrador.exe';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 503) setError('El migrador aun no esta publicado. Intenta de nuevo en unos minutos.');
      else setError('No se pudo descargar el migrador.');
    } finally {
      setCargando(false);
    }
  };

  const btn = {
    background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
    fontFamily: 'inherit',
  };

  return (
    <div className="cfg-card" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #eef0f4' }}>
      <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
        Migración desde ADVIN (Firebird)
      </div>
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '18px', maxWidth: '620px', lineHeight: 1.5 }}>
        Descarga <b>AgoraxMigrador.exe</b>, ábrelo en el equipo donde está el archivo <b>.fdb</b> de ADVIN,
        indica la ruta del archivo y pega el código que generas aquí. Se migran <b>clientes e inventario</b>;
        los clientes con deuda pendiente se migran con su deuda (facturas en espera). La primera vez
        migra todo; las siguientes solo cargan los cambios.
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <button onClick={descargar} disabled={cargando} style={{ ...btn, background: '#0B1A4A', opacity: cargando ? 0.5 : 1 }}>
          ⬇ Descargar AgoraxMigrador.exe
        </button>
        <select
          value={sedeSel}
          onChange={(e) => setSedeSel(e.target.value)}
          style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit' }}
        >
          {sedes.map((s) => (
            <option key={s.codigo} value={s.codigo}>{s.nombre} ({s.codigo})</option>
          ))}
        </select>
        <button onClick={generar} disabled={generando || !sedeSel} style={{ ...btn, opacity: generando || !sedeSel ? 0.5 : 1 }}>
          {generando ? 'Generando...' : 'Generar código de migración'}
        </button>
      </div>

      {codigo && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', color: '#15803d', fontWeight: 700, marginBottom: '6px' }}>
            Código de migración (válido por {expira || 24} horas, un solo uso)
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              readOnly
              value={codigo}
              onFocus={(e) => e.target.select()}
              style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '13px', fontFamily: 'Consolas, monospace' }}
            />
            <button onClick={copiar} style={{ ...btn, background: '#15803d' }}>Copiar</button>
          </div>
        </div>
      )}

      {historial.length > 0 && (
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Migraciones anteriores</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eef0f4', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Fecha</th>
                <th style={{ padding: '8px' }}>Sede</th>
                <th style={{ padding: '8px' }}>Tipo</th>
                <th style={{ padding: '8px' }}>Estado</th>
                <th style={{ padding: '8px' }}>Conteos</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((h) => (
                <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px' }}>{h.creado ? new Date(h.creado).toLocaleString() : '-'}</td>
                  <td style={{ padding: '8px' }}>{h.sede_codigo}</td>
                  <td style={{ padding: '8px' }}>{h.es_full ? 'Completa' : 'Incremental'}</td>
                  <td style={{ padding: '8px', color: h.estado === 'exito' ? '#15803d' : '#b91c1c' }}>{h.estado}</td>
                  <td style={{ padding: '8px', color: '#6b7280' }}>
                    {JSON.stringify(h.conteos || {}).slice(0, 80)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ TAB: PERFIL ════════════════════ */
function TabPerfil({ usuario, getRolLabel, sedeInfo, rbacRoleColor, rolColor }) {
  const [perfil, setPerfil] = useState({ nombre: '', usuario: '', correo: '' });
  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [guardando, setGuardando] = useState(false);
  const { sonidoActivo, toggleSonido } = useNotificaciones();

  useEffect(() => {
    setPerfil({
      nombre: usuario?.nombre || '',
      usuario: usuario?.usuario || '',
      correo: usuario?.correo || '',
    });
  }, [usuario]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', perfil.nombre);
      params.append('usuario', perfil.usuario);
      params.append('correo', perfil.correo);
      if (claveActual && claveNueva) {
        params.append('clave_actual', claveActual);
        params.append('clave_nueva', claveNueva);
      }
      await API.put(`/auth/perfil?${params.toString()}`);
      localStorage.setItem(
        'usuario',
        JSON.stringify({
          ...usuario,
          nombre: perfil.nombre,
          usuario: perfil.usuario,
          correo: perfil.correo,
        })
      );
      setClaveActual('');
      setClaveNueva('');
      alert('Perfil actualizado');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar perfil');
    } finally {
      setGuardando(false);
    }
  };

  const rolNombre = getRolLabel();
  const esRBAC = !!usuario?.rol_nombre;
  const badgeColor = esRBAC ? rbacRoleColor : rolColor[usuario?.rol] || '#6b7280';

  return (
    <div className="cfg-card" style={S.card}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '14px',
          marginBottom: '20px',
          padding: '16px',
          background: 'var(--agx-bg2)',
          borderRadius: '12px',
        }}
      >
        <div>
          <div style={S.label}>Rol</div>
          <span
            style={{
              padding: '4px 14px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              background: badgeColor + '18',
              color: badgeColor,
            }}
          >
            {rolNombre}
          </span>
        </div>
        <div>
          <div style={S.label}>Cedula</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--agx-text)' }}>
            {usuario?.cedula}
          </div>
        </div>
        {sedeInfo && (
          <div>
            <div style={S.label}>Sede</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--agx-muted)' }}>
              {sedeInfo.nombre} ({sedeInfo.codigo})
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '14px',
          marginBottom: '14px',
        }}
      >
        <div>
          <label style={S.label}>Nombre</label>
          <input
            value={perfil.nombre}
            onChange={(e) => setPerfil({ ...perfil, nombre: e.target.value })}
            style={S.input}
          />
        </div>
        <div>
          <label style={S.label}>Usuario</label>
          <input
            value={perfil.usuario}
            onChange={(e) => setPerfil({ ...perfil, usuario: e.target.value })}
            style={S.input}
          />
        </div>
      </div>
      <div style={{ marginBottom: '14px' }}>
        <label style={S.label}>Correo</label>
        <input
          value={perfil.correo}
          onChange={(e) => setPerfil({ ...perfil, correo: e.target.value })}
          style={S.input}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '14px',
          paddingTop: '14px',
          borderTop: '1px solid #f3f4f6',
        }}
      >
        <div>
          <label style={S.label}>Contraseña actual</label>
          <input
            type="password"
            value={claveActual}
            onChange={(e) => setClaveActual(e.target.value)}
            style={S.input}
            placeholder="Solo para cambiar"
          />
        </div>
        <div>
          <label style={S.label}>Contraseña nueva</label>
          <input
            type="password"
            value={claveNueva}
            onChange={(e) => setClaveNueva(e.target.value)}
            style={S.input}
            placeholder="Nueva contraseña"
          />
        </div>
      </div>
      <div
        style={{
          marginTop: '18px',
          padding: '16px',
          background: 'var(--agx-bg2)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--agx-text)' }}>
            Sonido de notificaciones
          </div>
          <div style={{ fontSize: '12px', color: 'var(--agx-muted)', marginTop: '2px' }}>
            Reproduce un sonido fuerte cuando un pedido entra a tus areas (cocina, armado, delivery
            y panel de pedidos).
          </div>
        </div>
        <button
          onClick={toggleSonido}
          style={{
            border: 'none',
            cursor: 'pointer',
            borderRadius: '20px',
            padding: '8px 20px',
            fontSize: '13px',
            fontWeight: 700,
            fontFamily: 'inherit',
            background: sonidoActivo ? '#34a853' : '#e0e0e0',
            color: sonidoActivo ? '#fff' : '#6b7280',
            transition: 'background 0.15s',
          }}
        >
          {sonidoActivo ? 'Activado' : 'Silenciado'}
        </button>
      </div>

      <div style={{ marginTop: '18px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--agx-text)', marginBottom: '8px' }}>
          Dispositivos vinculados
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-muted)', marginBottom: '12px' }}>
          Dispositivos con tu sesión iniciada. Úsalos para tomar fotos o escanear códigos desde el celular.
        </div>
        <DispositivosVinculados />
      </div>

      <div style={{ marginTop: '18px', textAlign: 'right' }}>
        <button onClick={guardar} disabled={guardando} style={S.btnPrimary}>
          {guardando ? 'Guardando...' : 'Guardar perfil'}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════ TAB: APARIENCIA ════════════════════ */
function TabApariencia({ usuario, API_URL }) {
  const [form, setForm] = useState({
    color_primario: '#0B1A4A',
    color_secundario: '#2952CC',
    color_fondo: '#F5F7FA',
    color_exito: '#34a853',
    color_peligro: '#ea4335',
    nombre_app: '',
    logo_url: '',
  });
  const [guardando, setGuardando] = useState(false);
  const logoInputRef = useRef(null);
  const [instalada, setInstalada] = useState(
    !!window.__agoraxInstalada || window.matchMedia('(display-mode: standalone)').matches
  );
  const [instalando, setInstalando] = useState(false);
  const [msgInstalar, setMsgInstalar] = useState(null);

  useEffect(() => {
    cargar();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalada(true);
    }
    const onInstalled = () => setInstalada(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  const cargar = async () => {
    try {
      const res = await API.get('/empresa-config/');
      if (res.data.status === 'success' && res.data.data) {
        const d = res.data.data;
        setForm({
          color_primario: d.color_primario || '#0B1A4A',
          color_secundario: d.color_secundario || '#2952CC',
          color_fondo: d.color_fondo || '#F5F7FA',
          color_exito: d.color_exito || '#34a853',
          color_peligro: d.color_peligro || '#ea4335',
          nombre_app: d.nombre_app || '',
          logo_url: d.logo_url || '',
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const params = new URLSearchParams();
      params.append('color_primario', form.color_primario);
      params.append('color_secundario', form.color_secundario);
      params.append('color_fondo', form.color_fondo);
      params.append('color_exito', form.color_exito);
      params.append('color_peligro', form.color_peligro);
      if (form.nombre_app) params.append('nombre_app', form.nombre_app);
      if (form.logo_url) params.append('logo_url', form.logo_url);
      await API.put(`/empresa-config/?${params.toString()}`);
      alert('Apariencia guardada');
    } catch (e) {
      alert('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const instalarApp = () => {
    if (instalada) return;
    setMsgInstalar(null);
    setInstalando(true);
    const fn = window.__agoraxInstalar;
    if (typeof fn === 'function') {
      const ok = fn();
      if (!ok) {
        setMsgInstalar({
          tipo: 'error',
          texto:
            'No se pudo instalar. Asegurate de usar Chrome o Edge y que la pagina ya haya cargado completamente. Intenta de nuevo.',
        });
      }
    } else {
      setMsgInstalar({
        tipo: 'error',
        texto: 'La instalacion no esta disponible en este navegador. Usa Chrome o Edge.',
      });
    }
    setTimeout(() => setInstalando(false), 1500);
  };

  const subirLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const res = await API.post('/empresa-config/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.status === 'success')
        setForm((prev) => ({ ...prev, logo_url: res.data.data.logo_url }));
    } catch (err) {
      alert('Error al subir logo');
    }
  };

  const logoFullUrl = form.logo_url ? getImagenUrl(form.logo_url) : null;

  const isDark = (hex) => {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
  };

  return (
    <div className="cfg-card" style={S.card}>
      {/* Preview */}
      <div
        style={{
          marginBottom: '24px',
          padding: '16px 20px',
          borderRadius: '12px',
          background: 'var(--agx-bg2)',
          border: '1px solid #eef0f4',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--agx-faint)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '12px',
          }}
        >
          Vista previa
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              background: form.color_primario,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '18px',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {form.nombre_app ? form.nombre_app.charAt(0).toUpperCase() : 'A'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>
              {form.nombre_app || 'Nombre de tu app'}
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: form.color_primario,
                  color: '#fff',
                }}
              >
                Primario
              </span>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: form.color_secundario,
                  color: '#fff',
                }}
              >
                Secundario
              </span>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: form.color_exito,
                  color: '#fff',
                }}
              >
                Exito
              </span>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: form.color_peligro,
                  color: '#fff',
                }}
              >
                Peligro
              </span>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: form.color_fondo,
                  color: isDark(form.color_fondo) ? '#fff' : '#111',
                  border: '1px solid #ddd',
                }}
              >
                Fondo
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Instalar App */}
      <div
        style={{
          marginBottom: '16px',
          padding: '14px 16px',
          borderRadius: '10px',
          background: '#f0f4ff',
          border: '1px solid #d0d9f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'var(--agx-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <img
              src="/logo-agorax.jpeg"
              alt="Agorax"
              style={{ width: '30px', height: '30px', borderRadius: '6px', objectFit: 'contain' }}
            />
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-text)' }}>
              App de escritorio
            </div>
            <div style={{ fontSize: '11px', color: 'var(--agx-muted)' }}>
              {instalada
                ? 'La app ya esta instalada en este dispositivo'
                : 'Instala Agorax como aplicacion para acceder mas rapido'}
            </div>
          </div>
        </div>
        <button
          onClick={instalarApp}
          disabled={instalada}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: instalada ? 'not-allowed' : 'pointer',
            opacity: instalada ? 0.5 : 1,
            background: instalada ? '#059669' : '#2563eb',
            color: '#fff',
            whiteSpace: 'nowrap',
          }}
        >
          {instalada ? (
            <>
              <FiCheck size={14} /> Ya instalada
            </>
          ) : instalando ? (
            'Instalando...'
          ) : (
            <>
              <FiDownload size={14} /> Instalar App
            </>
          )}
        </button>
        {msgInstalar && (
          <div
            style={{
              width: '100%',
              marginTop: '6px',
              fontSize: '11px',
              fontWeight: 500,
              color: msgInstalar.tipo === 'error' ? '#dc2626' : '#059669',
              background: msgInstalar.tipo === 'error' ? '#fef2f2' : '#ecfdf5',
              padding: '8px 12px',
              borderRadius: '8px',
              border: msgInstalar.tipo === 'error' ? '1px solid #fecaca' : '1px solid #a7f3d0',
            }}
          >
            {msgInstalar.texto}
          </div>
        )}
      </div>

      {/* Nombre y Logo */}
      <div
        style={{ paddingBottom: '16px', borderBottom: '1px solid #f3f4f6', marginBottom: '16px' }}
      >
        <div style={{ marginBottom: '14px' }}>
          <label style={S.label}>Nombre en la App</label>
          <input
            value={form.nombre_app}
            onChange={(e) => setForm({ ...form, nombre_app: e.target.value })}
            style={S.input}
            placeholder={usuario?.nombre_empresa || 'Nombre de la empresa'}
          />
          <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '3px' }}>
            Se muestra en el navegador, emails y notificaciones a clientes
          </div>
        </div>
        <label style={S.label}>Logo</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {logoFullUrl ? (
            <img
              src={logoFullUrl}
              alt="Logo"
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                objectFit: 'cover',
                border: '1px solid #e5e7eb',
              }}
            />
          ) : (
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: '#f3f4f6',
                border: '2px dashed #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--agx-faint)',
                fontSize: '11px',
              }}
            >
              Sin logo
            </div>
          )}
          <button onClick={() => logoInputRef.current?.click()} style={S.btnOutline}>
            Subir logo
          </button>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={subirLogo}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Plantillas */}
      <div style={{ marginBottom: '14px' }}>
        <div
          style={{ ...S.label, fontWeight: 700, color: 'var(--agx-muted)', marginBottom: '8px' }}
        >
          Plantillas de color
        </div>
        <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginBottom: '10px' }}>
          Selecciona una combinacion predefinida o personaliza manualmente
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          {PLANTILLAS.map((p) => {
            const selected =
              form.color_primario === p.primario && form.color_secundario === p.secundario;
            return (
              <div
                key={p.nombre}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    color_primario: p.primario,
                    color_secundario: p.secundario,
                    color_fondo: p.fondo,
                    color_exito: p.exito,
                    color_peligro: p.peligro,
                  }))
                }
                style={{
                  cursor: 'pointer',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: selected ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  background: selected ? '#eff6ff' : '#fff',
                  transition: 'all 0.12s',
                }}
              >
                <div style={{ marginBottom: '6px' }}>{p.nombre}</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[p.primario, p.secundario, p.exito, p.peligro, p.fondo].map((c, i) => (
                    <div
                      key={i}
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '5px',
                        backgroundColor: c,
                        border:
                          c === '#FAFAFA' ||
                          c === '#F5F6F8' ||
                          c === '#F5F7FA' ||
                          c === '#F8F9FE' ||
                          c === '#F4F5FC'
                            ? '1px solid #ddd'
                            : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Colores individuales */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{ ...S.label, fontWeight: 700, color: 'var(--agx-muted)', marginBottom: '8px' }}
        >
          Ajuste fino
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {COLOR_LABELS.map((c) => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="color"
                value={form[c.key]}
                onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
              <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--agx-muted)' }}>
                {c.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <button onClick={guardar} disabled={guardando} style={S.btnPrimary}>
          {guardando ? 'Guardando...' : 'Guardar apariencia'}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════ TAB: OPERACION ════════════════════ */
function TabOperacion() {
  const [form, setForm] = useState({
    tipos_delivery: ['retiro'],
    modo: 'ambos',
    modo_registro: 'simple',
    confirmacion_cliente_habilitada: true,
    tiene_restaurante: false,
    tiene_armado: false,
    usa_estado_servido: false,
    cierre_caja_obligatorio: false,
    permite_espera_sin_mesa: true,
    permite_pedidos_simultaneos: true,
    requiere_ubicacion_cliente: true,
    mostrar_precio_bs: true,
    permitir_venta_agotados: false,
    mostrar_agotados: true,
    notas_personalizacion_habilitadas: true,
    credito_habilitado: false,
    credito_plazo_dias: 30,
    cliente_puede_modificar_en_revision: false,
    aprobacion_registro_habilitada: false,
  });
  const [guardando, setGuardando] = useState(false);
  const [botMetrics, setBotMetrics] = useState(null);
  const [cargandoMetrics, setCargandoMetrics] = useState(false);
  const [showBotMetrics, setShowBotMetrics] = useState(false);

  useEffect(() => {
    cargar();
  }, []); // eslint-disable-line

  const cargar = async () => {
    try {
      const res = await API.get('/empresa-config/');
      if (res.data.status === 'success' && res.data.data) {
        const d = res.data.data;
        let td = d.tipos_delivery;
        if (typeof td === 'string') {
          try {
            td = JSON.parse(td);
          } catch {
            td = null;
          }
        }
        const cat = d.catalogo_habilitado,
          ch = d.chat_habilitado;
        const modo = cat && !ch ? 'solo_catalogo' : !cat && ch ? 'solo_chat' : 'ambos';
        setForm({
          tipos_delivery: Array.isArray(td) && td.length ? td : ['retiro'],
          modo,
          modo_registro: d.modo_registro || 'simple',
          confirmacion_cliente_habilitada: d.confirmacion_cliente_habilitada !== false,
          tiene_restaurante: d.tiene_restaurante === true,
          tiene_armado: d.tiene_armado === true,
          usa_estado_servido: d.usa_estado_servido === true,
          cierre_caja_obligatorio: d.cierre_caja_obligatorio === true,
          permite_espera_sin_mesa: d.permite_espera_sin_mesa !== false,
          permite_pedidos_simultaneos: d.permite_pedidos_simultaneos !== false,
          requiere_ubicacion_cliente: d.requiere_ubicacion_cliente !== false,
          mostrar_precio_bs: d.mostrar_precio_bs !== false,
          permitir_venta_agotados: d.permitir_venta_agotados === true,
          mostrar_agotados: d.mostrar_agotados !== false,
          notas_personalizacion_habilitadas: d.notas_personalizacion_habilitadas !== false,
          credito_habilitado: d.credito_habilitado === true,
          credito_plazo_dias: d.credito_plazo_dias || 30,
          cliente_puede_modificar_en_revision: d.cliente_puede_modificar_en_revision === true,
          aprobacion_registro_habilitada: d.aprobacion_registro_habilitada === true,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleTipoEntrega = (t) => {
    setForm((prev) => {
      const has = prev.tipos_delivery.includes(t);
      const next = has ? prev.tipos_delivery.filter((x) => x !== t) : [...prev.tipos_delivery, t];
      return { ...prev, tipos_delivery: next };
    });
  };

  const guardar = async () => {
    if (form.tipos_delivery.length === 0) {
      alert('Selecciona al menos un método de entrega');
      return;
    }
    setGuardando(true);
    try {
      const params = new URLSearchParams();
      form.tipos_delivery.forEach((t) => params.append('tipos_delivery', t));
      params.append('modo', form.modo);
      params.append('modo_registro', form.modo_registro);
      params.append('confirmacion_cliente_habilitada', form.confirmacion_cliente_habilitada);
      params.append('tiene_restaurante', form.tiene_restaurante);
      params.append('tiene_armado', form.tiene_armado);
      params.append('usa_estado_servido', form.usa_estado_servido);
      params.append('cierre_caja_obligatorio', form.cierre_caja_obligatorio);
      params.append('permite_espera_sin_mesa', form.permite_espera_sin_mesa);
      params.append('permite_pedidos_simultaneos', form.permite_pedidos_simultaneos);
      params.append('requiere_ubicacion_cliente', form.requiere_ubicacion_cliente);
      params.append('mostrar_precio_bs', form.mostrar_precio_bs);
      params.append('permitir_venta_agotados', form.permitir_venta_agotados);
      params.append('mostrar_agotados', form.mostrar_agotados);
      params.append('notas_personalizacion_habilitadas', form.notas_personalizacion_habilitadas);
      params.append('credito_habilitado', form.credito_habilitado);
      params.append('credito_plazo_dias', form.credito_plazo_dias);
      params.append(
        'cliente_puede_modificar_en_revision',
        form.cliente_puede_modificar_en_revision
      );
      params.append('aprobacion_registro_habilitada', form.aprobacion_registro_habilitada);
      await API.put(`/empresa-config/?${params.toString()}`);
      alert('Operacion guardada');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const cargarBotMetrics = async () => {
    setCargandoMetrics(true);
    try {
      const res = await API.get('/bot/metricas/calidad?horas=24');
      if (res.data.status === 'success') setBotMetrics(res.data.data);
    } catch {
      setBotMetrics({ error: true });
    } finally {
      setCargandoMetrics(false);
    }
  };

  useEffect(() => {
    if (showBotMetrics) cargarBotMetrics();
  }, [showBotMetrics]);

  return (
    <div className="cfg-card" style={S.card}>
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Métodos de entrega
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Opciones que el cliente puede elegir al confirmar su pedido
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {DELIVERY_OPTS.map((opt) => {
            const sel = form.tipos_delivery.includes(opt.v);
            return (
              <button
                key={opt.v}
                onClick={() => toggleTipoEntrega(opt.v)}
                style={{
                  padding: '12px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  background: sel ? '#eff6ff' : '#fff',
                  color: sel ? '#2563eb' : '#374151',
                  textAlign: 'left',
                  minWidth: '150px',
                }}
              >
                <div>{opt.l}</div>
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 400,
                    color: sel ? '#2563eb' : '#9ca3af',
                    marginTop: '2px',
                  }}
                >
                  {opt.d}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Modo de atención
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Que experiencia tendra el cliente al visitar tu tienda
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {MODO_OPTS.map((opt) => {
            const sel = form.modo === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setForm((prev) => ({ ...prev, modo: opt.v }))}
                style={{
                  padding: '12px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  background: sel ? '#eff6ff' : '#fff',
                  color: sel ? '#2563eb' : '#374151',
                  textAlign: 'left',
                  minWidth: '160px',
                }}
              >
                <div>{opt.l}</div>
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 400,
                    color: sel ? '#2563eb' : '#9ca3af',
                    marginTop: '2px',
                  }}
                >
                  {opt.d}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Requisitos del pedido
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Configura que informacion necesitas del cliente para iniciar un pedido
        </div>
        <ToggleFlag
          label="Requerir ubicacion GPS"
          desc="El cliente debe compartir su ubicacion para poder iniciar un pedido. Si se desactiva, el cliente puede elegir una sede manualmente o sera asignado automaticamente."
          value={form.requiere_ubicacion_cliente}
          onChange={(v) => setForm((prev) => ({ ...prev, requiere_ubicacion_cliente: v }))}
        />
        <ToggleFlag
          label="Mostrar precio en Bs"
          desc="Muestra el precio en bolivares junto al precio en dolares en el catalogo del cliente. Usa la tasa de cambio configurada."
          value={form.mostrar_precio_bs}
          onChange={(v) => setForm((prev) => ({ ...prev, mostrar_precio_bs: v }))}
        />
        <ToggleFlag
          label="Permitir venta de productos agotados"
          desc="Los clientes podran ver y agregar al carrito productos con stock 0 o negativo. Los productos agotados apareceran con el stock real en lugar de 'Agotado'."
          value={form.permitir_venta_agotados}
          onChange={(v) => setForm((prev) => ({ ...prev, permitir_venta_agotados: v }))}
        />
        <ToggleFlag
          label="Mostrar productos agotados en el catalogo"
          desc="Si se desactiva, los productos con stock 0 o negativo no se muestran en el catalogo del cliente. Si esta activada la venta de agotados, siempre se muestran."
          value={form.mostrar_agotados}
          onChange={(v) => setForm((prev) => ({ ...prev, mostrar_agotados: v }))}
        />
        <ToggleFlag
          label="Permitir notas de personalizacion"
          desc="Muestra la seccion 'Agregar una nota' en el detalle de producto del catalogo del cliente, para que envie instrucciones libres (ej. sin cebolla, envolver para regalo)."
          value={form.notas_personalizacion_habilitadas}
          onChange={(v) => setForm((prev) => ({ ...prev, notas_personalizacion_habilitadas: v }))}
        />
        <ToggleFlag
          label="Permitir modificaciones en revision"
          desc="Los clientes podran agregar, eliminar o modificar productos durante la fase de revision. Cada cambio se notificara al asesor."
          value={form.cliente_puede_modificar_en_revision}
          onChange={(v) => setForm((prev) => ({ ...prev, cliente_puede_modificar_en_revision: v }))}
        />
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowBotMetrics(!showBotMetrics)}>
          <div>
            <div style={{ ...S.label, fontWeight: 700, color: 'var(--agx-muted)', fontSize: '14px', marginBottom: '4px' }}>Calidad del Bot IA</div>
            <div style={{ fontSize: '12px', color: 'var(--agx-faint)' }}>Metricas de exito, fallos y autocorrecciones del asistente virtual</div>
          </div>
          <span style={{ fontSize: '18px', color: 'var(--agx-muted)' }}>{showBotMetrics ? '\u25BC' : '\u25B6'}</span>
        </div>
        {showBotMetrics && (
          <div style={{ marginTop: '12px' }}>
            {cargandoMetrics ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--agx-faint)', fontSize: '13px' }}>Cargando metricas...</div>
            ) : botMetrics?.error ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#ea4335', fontSize: '13px' }}>
                No se pudieron cargar las metricas. <button onClick={cargarBotMetrics} style={{ background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', textDecoration: 'underline' }}>Reintentar</button>
              </div>
            ) : !botMetrics ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--agx-faint)', fontSize: '13px' }}>Aun no hay datos. Las metricas se generan cuando el bot interactua con clientes.</div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '100px', background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: botMetrics.resumen.tasa_exito >= 0.8 ? '#34a853' : botMetrics.resumen.tasa_exito >= 0.5 ? '#ea8600' : '#ea4335' }}>{(botMetrics.resumen.tasa_exito * 100).toFixed(0)}%</div>
                    <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>Tasa de exito</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--agx-text)' }}>{botMetrics.resumen.total_llamadas}</div>
                    <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>Llamadas (24h)</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '100px', background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: botMetrics.resumen.fallos > 0 ? '#ea4335' : '#34a853' }}>{botMetrics.resumen.fallos}</div>
                    <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>Fallos</div>
                  </div>
                </div>
                {botMetrics.intents?.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-muted)', marginBottom: '6px' }}>Intents clasificados</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {botMetrics.intents.slice(0, 5).map((int, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', flex: 1, color: 'var(--agx-text)' }}>{int.intent}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--agx-muted)' }}>{int.count}</span>
                          <div style={{ flex: 1, height: '6px', background: '#f1f3f4', borderRadius: '3px', maxWidth: '100px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: Math.min(100, (int.count / Math.max(1, botMetrics.intents[0].count || 1)) * 100) + '%', background: '#1a73e8', borderRadius: '3px' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {botMetrics.autocorrecciones?.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#34a853', marginBottom: '6px' }}>Autocorrecciones aplicadas</div>
                    {botMetrics.autocorrecciones.slice(0, 6).map((ac, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--agx-muted)', padding: '2px 0' }}>
                        <span>{ac.tipo || 'sin tipo'}</span><span style={{ fontWeight: 500 }}>{ac.count}</span>
                      </div>
                    ))}
                  </div>
                )}
                {botMetrics.ultimos_errores?.length > 0 && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#ea4335', marginBottom: '6px' }}>Ultimos errores ({botMetrics.ultimos_errores.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
                      {botMetrics.ultimos_errores.slice(0, 8).map((err, i) => (
                        <div key={i} style={{ padding: '6px 8px', borderRadius: '6px', fontSize: '11px', background: err.autocorregido ? '#e8f5e9' : err.severidad === 'high' ? '#fce8e6' : '#fef7e0', color: err.autocorregido ? '#34a853' : '#ea4335' }}>
                          <div style={{ fontWeight: 500 }}>{err.tipo} {err.categoria ? '(' + err.categoria + ')' : ''}</div>
                          <div style={{ opacity: 0.7 }}>{err.detalle}</div>
                          <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.5 }}>{err.fecha ? new Date(err.fecha).toLocaleString() : ''} {err.autocorregido ? '| Autocorregido' : ''}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={cargarBotMetrics} style={{ marginTop: '12px', padding: '6px 14px', borderRadius: '8px', border: '1px solid #dadce0', background: '#fff', color: 'var(--agx-muted)', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: 'inherit' }}>
                  <FiRefreshCw size={12} style={{ marginRight: '4px' }} /> Actualizar metricas
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Registro de clientes
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Como se identificaran los clientes al visitar tu tienda
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {MODO_REGISTRO_OPTS.map((opt) => {
            const sel = form.modo_registro === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setForm((prev) => ({ ...prev, modo_registro: opt.v }))}
                style={{
                  padding: '12px 18px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  background: sel ? '#eff6ff' : '#fff',
                  color: sel ? '#2563eb' : '#374151',
                  textAlign: 'left',
                  minWidth: '160px',
                  flex: '1 1 200px',
                  maxWidth: '380px',
                }}
              >
                <div>{opt.l}</div>
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 400,
                    color: sel ? '#2563eb' : '#9ca3af',
                    marginTop: '2px',
                  }}
                >
                  {opt.d}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Confirmacion al cliente en revision
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Cuando el asesor modifica un pedido en revision, el cliente debe confirmar los cambios
          antes de pasar a pago
        </div>
        <button
          onClick={() =>
            setForm((prev) => ({
              ...prev,
              confirmacion_cliente_habilitada: !prev.confirmacion_cliente_habilitada,
            }))
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 18px',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            border: form.confirmacion_cliente_habilitada
              ? '2px solid #2563eb'
              : '1px solid #e5e7eb',
            background: form.confirmacion_cliente_habilitada ? '#eff6ff' : '#fff',
            color: 'var(--agx-muted)',
            textAlign: 'left',
            minWidth: '300px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: form.confirmacion_cliente_habilitada ? '#34a853' : '#d1d5db',
              position: 'relative',
              transition: 'background-color 0.2s',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: 'var(--agx-surface)',
                position: 'absolute',
                top: '2px',
                left: form.confirmacion_cliente_habilitada ? '22px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }}
            />
          </div>
          <div>
            <div>{form.confirmacion_cliente_habilitada ? 'Habilitada' : 'Deshabilitada'}</div>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 400,
                color: 'var(--agx-faint)',
                marginTop: '2px',
              }}
            >
              {form.confirmacion_cliente_habilitada
                ? 'El cliente debera confirmar los cambios antes de pagar'
                : 'El pedido pasara directamente a esperando pago sin confirmacion del cliente'}
            </div>
          </div>
        </button>
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6', marginTop: '12px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Aprobacion de registro de clientes
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Cuando un cliente se registra con el formulario completo, debera esperar a que la
          empresa apruebe su solicitud antes de poder acceder. Al desactivar esta opcion se
          aprueban automaticamente las solicitudes pendientes.
        </div>
        <ToggleFlag
          label="Requiere aprobacion de la empresa"
          desc="Los nuevos clientes quedan en revision hasta que un administrador los apruebe"
          value={form.aprobacion_registro_habilitada}
          onChange={(v) => setForm((prev) => ({ ...prev, aprobacion_registro_habilitada: v }))}
        />
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6', marginTop: '12px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Funcionalidades de restaurante
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Configura mesas, cocina y flujo de pedidos en tienda
        </div>

        <ToggleFlag
          label="Es restaurante"
          desc="Habilita salon de mesas, comandas y flujo de cocina"
          value={form.tiene_restaurante}
          onChange={(v) => setForm((prev) => ({ ...prev, tiene_restaurante: v }))}
        />
        <ToggleFlag
          label="Usar armado/cocina"
          desc="Los pedidos pasan por el panel de armado antes de entregarse"
          value={form.tiene_armado}
          onChange={(v) => setForm((prev) => ({ ...prev, tiene_armado: v }))}
        />

        {form.tiene_restaurante && form.tiene_armado && (
          <ToggleFlag
            label="Usar estado 'Servido'"
            desc="Agrega el estado servido entre listo y cobrado (mesero confirma entrega)"
            value={form.usa_estado_servido}
            onChange={(v) => setForm((prev) => ({ ...prev, usa_estado_servido: v }))}
          />
        )}

        {form.tiene_restaurante && (
          <ToggleFlag
            label="Permitir espera sin mesa"
            desc="Guardar pedidos en espera sin asignar mesa obligatoriamente"
            value={form.permite_espera_sin_mesa}
            onChange={(v) => setForm((prev) => ({ ...prev, permite_espera_sin_mesa: v }))}
          />
        )}

        <ToggleFlag
          label="Permitir pedidos simultaneos"
          desc="El cliente puede tener varios pedidos en curso al mismo tiempo. Si se desactiva, solo puede haber un pedido activo a la vez"
          value={form.permite_pedidos_simultaneos}
          onChange={(v) => setForm((prev) => ({ ...prev, permite_pedidos_simultaneos: v }))}
        />
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6', marginTop: '12px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Cierre de caja
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Control de cobros al final del dia laboral por usuario
        </div>

        <ToggleFlag
          label="Cierre de caja obligatorio"
          desc="Bloquea nuevos cobros hasta que el usuario revise el dia laboral anterior"
          value={form.cierre_caja_obligatorio}
          onChange={(v) => setForm((prev) => ({ ...prev, cierre_caja_obligatorio: v }))}
        />
      </div>

      <div style={{ paddingTop: '20px', borderTop: '1px solid #f3f4f6', marginTop: '12px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Sistema de credito
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '12px' }}>
          Permite a clientes registrados pedir a credito con plazo de pago configurable (solo modo
          de registro completo)
        </div>

        <ToggleFlag
          label="Habilitar credito"
          desc="Los clientes podran elegir Credito como condicion de venta al confirmar su pedido"
          value={form.credito_habilitado}
          onChange={(v) => setForm((prev) => ({ ...prev, credito_habilitado: v }))}
        />

        {form.credito_habilitado && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ ...S.label, marginBottom: '6px' }}>Plazo de pago (dias)</div>
            <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '8px' }}>
              Dias que tiene el cliente para pagar despues de recibir el pedido
            </div>
            <input
              type="number"
              min="1"
              max="365"
              value={form.credito_plazo_dias}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, credito_plazo_dias: parseInt(e.target.value) || 30 }))
              }
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid var(--agx-border)',
                fontSize: '14px',
                width: '120px',
                background: 'var(--agx-surface)',
                color: 'var(--agx-text)',
              }}
            />
          </div>
        )}
      </div>

      <div style={{ textAlign: 'right', marginTop: '20px' }}>
        <button onClick={guardar} disabled={guardando} style={S.btnPrimary}>
          {guardando ? 'Guardando...' : 'Guardar operacion'}
        </button>
      </div>
    </div>
  );
}

function ToggleFlag({ label, desc, value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 18px',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        border: value ? '2px solid #2563eb' : '1px solid #e5e7eb',
        background: value ? '#eff6ff' : '#fff',
        color: 'var(--agx-muted)',
        textAlign: 'left',
        width: '100%',
        marginBottom: '8px',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: '44px',
          height: '24px',
          borderRadius: '12px',
          backgroundColor: value ? '#34a853' : '#d1d5db',
          position: 'relative',
          transition: 'background-color 0.2s',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: 'var(--agx-surface)',
            position: 'absolute',
            top: '2px',
            left: value ? '22px' : '2px',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </div>
      <div>
        <div>{label}</div>
        <div
          style={{ fontSize: '10px', fontWeight: 400, color: 'var(--agx-faint)', marginTop: '2px' }}
        >
          {desc}
        </div>
      </div>
    </button>
  );
}

/* ════════════════════ TAB: TICKETS ════════════════════ */
const BLOQUE_LABEL = { cliente: 'Datos del cliente', notas: 'Notas', pie: 'Pie / Agradecimiento' };
const SECCIONES_LABEL = {
  empresa: 'Nombre de la empresa',
  sede: 'Sede',
  fecha_hora: 'Fecha y hora',
  cajero: 'Cajero',
  cliente: 'Datos del cliente',
  mesa: 'Número de mesa',
  ticket_numero: 'Número de ticket',
  pedido_global: 'Número de pedido global',
  adicionales: 'Adicionales / extras',
  notas: 'Notas',
  pie: 'Pie / agradecimiento',
};

function Section({ titulo, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--agx-border)', borderRadius: '12px', marginBottom: '12px', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '13px 16px',
          background: 'var(--agx-bg2)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--agx-text)',
        }}
      >
        <span>{titulo}</span>
        <FiChevronDown size={16} style={{ color: 'var(--agx-faint)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && <div style={{ padding: '14px 16px', borderTop: '1px solid var(--agx-border)' }}>{children}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 0',
        cursor: 'pointer',
        fontSize: '13px',
        color: 'var(--agx-text)',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          width: 38,
          height: 22,
          borderRadius: 999,
          background: checked ? '#2563eb' : 'var(--agx-skeleton)',
          position: 'relative',
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
            transition: 'left 0.15s',
          }}
        />
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: 'none' }}
      />
      {label}
    </label>
  );
}

function TabTickets() {
  const [cfg, setCfg] = useState(null);
  const [monedas, setMonedas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modoPreview, setModoPreview] = useState('entrega');

  const cargar = () => {
    setCargando(true);
    Promise.all([
      API.get('/empresa-config/ticket'),
      API.get('/ventas/monedas'),
      API.get('/empresa-config/'),
    ])
      .then(([cfgRes, monRes, empRes]) => {
        if (cfgRes.data?.status === 'success') setCfg(cfgRes.data.data);
        if (monRes.data?.status === 'success') setMonedas(monRes.data.data || []);
        if (empRes.data?.status === 'success') setEmpresa(empRes.data.data || null);
      })
      .catch((e) => console.error('Error cargando config de tickets:', e))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
  }, []);

  const set = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));
  const setMostrar = (key, value) =>
    setCfg((prev) => ({ ...prev, mostrar: { ...prev.mostrar, [key]: value } }));
  const setNota = (id, patch) =>
    setCfg((prev) => ({
      ...prev,
      notas: prev.notas.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  const agregarNota = () =>
    setCfg((prev) => ({
      ...prev,
      notas: [...prev.notas, { id: `n${Date.now()}`, texto: '', activa: true, resaltada: false }],
    }));
  const eliminarNota = (id) =>
    setCfg((prev) => ({ ...prev, notas: prev.notas.filter((n) => n.id !== id) }));
  const moverBloque = (idx, dir) =>
    setCfg((prev) => {
      const orden = [...prev.orden];
      const j = idx + dir;
      if (j < 0 || j >= orden.length) return prev;
      [orden[idx], orden[j]] = [orden[j], orden[idx]];
      return { ...prev, orden };
    });

  const subirLogo = async (file) => {
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const r = await API.post('/empresa-config/ticket/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.status === 'success') {
        setCfg((prev) => ({ ...prev, usar_logo: true, logo_url: r.data.data.logo_url }));
      }
    } catch (e) {
      alert('Error al subir el logo: ' + (e.response?.data?.detail || e.message));
    }
  };

  const usarLogoEmpresa = () => setCfg((prev) => ({ ...prev, logo_url: null, usar_logo: true }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        titulo_entrega: cfg.titulo_entrega,
        titulo_cobro: cfg.titulo_cobro,
        pie: cfg.pie,
        moneda_resaltada: cfg.moneda_resaltada || null,
        moneda_precios: cfg.moneda_precios || null,
        subtotal_moneda: cfg.subtotal_moneda || 'ambas',
        mostrar_precio_unidad: !!cfg.mostrar_precio_unidad,
        fuente: cfg.fuente,
        tamano: cfg.tamano,
        interlineado: cfg.interlineado,
        negrita: cfg.negrita,
        usar_logo: cfg.usar_logo,
        logo_url: cfg.logo_url || null,
        mostrar: cfg.mostrar,
        notas: cfg.notas.map((n) => ({ id: n.id, texto: n.texto, activa: n.activa, resaltada: n.resaltada })),
        orden: cfg.orden,
      };
      const r = await API.put('/empresa-config/ticket', payload);
      if (r.data?.status === 'success') setCfg(r.data.data);
      alert('Configuración de tickets guardada');
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando || !cfg) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--agx-faint)' }}>Cargando...</div>
    );
  }

  const principal = monedas.find((m) => m.es_principal);
  const secundarias = monedas.filter((m) => !m.es_principal);

  const previewRecibo = {
    empresa: empresa?.empresa_nombre || empresa?.nombre_app || 'Mi Empresa',
    logo_url: empresa?.logo_url || null,
    sede: 'Sede Principal',
    cajero: 'Ana',
    fecha: new Date().toISOString(),
    total: 26.68,
    subtotal: 23,
    impuesto: 3.68,
    impuesto_porcentaje: 16,
    cobrar_impuesto: true,
    id: 1234,
    ticket_numero: 4,
    mesa: '4',
    cliente: { nombre: 'Cliente de prueba', cedula: 'V-12345678', telefono: '0412-0000000' },
    monedas: monedas.map((m) => ({ codigo: m.codigo, nombre: m.nombre, simbolo: m.simbolo, tasa: m.tasa, principal: m.es_principal })),
    items: [
      { codigo: 'A1', nombre: 'Cappuccino', cantidad: 2, precio_unitario: 3, subtotal: 6, adicionales: [{ nombre: 'Leche extra', precio: 0.5 }], componentes: [], nota: 'sin azucar' },
      { codigo: 'A2', nombre: 'Agua Mineral', cantidad: 1, precio_unitario: 1, subtotal: 1, adicionales: [], componentes: [], nota: '' },
    ],
    ticket_config: cfg,
  };

  return (
    <div>
      <div style={S.card}>
        <div className="tickets-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div>
            <div style={{ ...S.label, fontWeight: 700, color: 'var(--agx-muted)', fontSize: '14px' }}>
              Personalización del ticket
            </div>
            <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginTop: '2px' }}>
              Configura cómo se ven tus tickets de entrega y cobro. La vista previa es exactamente lo que saldrá impreso.
            </div>
          </div>
          <button onClick={guardar} disabled={guardando} style={S.btnPrimary}>
            <FiSave size={14} /> {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>

        <div className="tickets-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.05fr', gap: '28px', alignItems: 'start' }}>
          {/* ── Configuración ── */}
          <div style={{ minWidth: 0 }}>
            <Section titulo="Títulos y textos" defaultOpen>
              <div style={S.label}>Título del ticket de entrega</div>
              <input style={{ ...S.input, marginBottom: '10px' }} value={cfg.titulo_entrega} onChange={(e) => set('titulo_entrega', e.target.value)} />
              <div style={S.label}>Título del ticket de cobro</div>
              <input style={{ ...S.input, marginBottom: '10px' }} value={cfg.titulo_cobro} onChange={(e) => set('titulo_cobro', e.target.value)} />
              <div style={S.label}>Pie / agradecimiento</div>
              <input style={S.input} value={cfg.pie} onChange={(e) => set('pie', e.target.value)} />
            </Section>

            <Section titulo="Apariencia del texto" defaultOpen>
              <div className="tickets-appear-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={S.label}>Tipo de letra</div>
                  <select style={{ ...S.input, marginBottom: '10px' }} value={cfg.fuente} onChange={(e) => set('fuente', e.target.value)}>
                    {FUENTES.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={S.label}>Tamaño: {cfg.tamano}px</div>
                  <input
                    type="range" min={8} max={24} value={cfg.tamano}
                    onChange={(e) => set('tamano', parseInt(e.target.value, 10))}
                    style={{ width: '100%', margin: '6px 0 10px', accentColor: '#2563eb' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '4px' }}>
                <div style={S.label}>Separación entre líneas: {cfg.interlineado}</div>
                <input
                  type="range" min={0.7} max={2.0} step={0.05} value={cfg.interlineado}
                  onChange={(e) => set('interlineado', parseFloat(e.target.value))}
                  style={{ width: '100%', margin: '6px 0', accentColor: '#2563eb' }}
                />
                <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '-2px' }}>
                  Baja el valor para pegar las líneas y ahorrar papel.
                </div>
              </div>
              <div style={{ marginTop: '8px' }}>
                <Toggle checked={!!cfg.negrita} onChange={(v) => set('negrita', v)} label="Texto en negrita (más oscuro y grueso)" />
              </div>
            </Section>

            <Section titulo="Logo del ticket" defaultOpen>
              <Toggle checked={!!cfg.usar_logo} onChange={(v) => set('usar_logo', v)} label="Mostrar logo" />
              {cfg.usar_logo && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {(cfg.logo_url || empresa?.logo_url) && (
                      <img
                        src={getImagenUrl(cfg.logo_url || empresa?.logo_url)}
                        alt="logo"
                        style={{ maxWidth: 90, maxHeight: 48, background: '#fff', border: '1px solid var(--agx-border)', borderRadius: 6, padding: 4 }}
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <label style={{ ...S.btnOutline, cursor: 'pointer', margin: 0 }}>
                        <FiPlus size={14} /> Subir logo personalizado
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) subirLogo(f); }}
                        />
                      </label>
                      {cfg.logo_url && (
                        <button onClick={usarLogoEmpresa} style={S.btnSm}>
                          <FiRefreshCw size={13} /> Usar logo de la empresa
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Section>

            <Section titulo="Secciones visibles" defaultOpen>
              {Object.keys(SECCIONES_LABEL).map((key) => (
                <Toggle key={key} checked={!!cfg.mostrar[key]} onChange={(v) => setMostrar(key, v)} label={SECCIONES_LABEL[key]} />
              ))}
            </Section>

            <Section titulo="Moneda resaltada en los totales" defaultOpen>
              <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '10px' }}>
                La moneda seleccionada se mostrará en negrita para destacarla.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button
                  onClick={() => set('moneda_resaltada', null)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: `1px solid ${cfg.moneda_resaltada == null ? '#2563eb' : 'var(--agx-border)'}`,
                    background: cfg.moneda_resaltada == null ? '#eff6ff' : 'transparent',
                    color: cfg.moneda_resaltada == null ? '#2563eb' : 'var(--agx-text)',
                    fontSize: 12,
                    fontWeight: cfg.moneda_resaltada == null ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Ninguna
                </button>
                {secundarias.map((m) => (
                  <button
                    key={m.codigo}
                    onClick={() => set('moneda_resaltada', m.codigo)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: `1px solid ${cfg.moneda_resaltada === m.codigo ? '#2563eb' : 'var(--agx-border)'}`,
                      background: cfg.moneda_resaltada === m.codigo ? '#eff6ff' : 'transparent',
                      color: cfg.moneda_resaltada === m.codigo ? '#2563eb' : 'var(--agx-text)',
                      fontSize: 12,
                      fontWeight: cfg.moneda_resaltada === m.codigo ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {m.simbolo} {m.nombre}
                  </button>
                ))}
              </div>
            </Section>

            <Section titulo="Moneda de los precios" defaultOpen>
              <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '10px' }}>
                Elige en qué moneda se muestran los precios del ticket (items, precio por unidad y totales).
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <button
                  onClick={() => set('moneda_precios', null)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: `1px solid ${cfg.moneda_precios == null ? '#2563eb' : 'var(--agx-border)'}`,
                    background: cfg.moneda_precios == null ? '#eff6ff' : 'transparent',
                    color: cfg.moneda_precios == null ? '#2563eb' : 'var(--agx-text)',
                    fontSize: 12,
                    fontWeight: cfg.moneda_precios == null ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {principal ? `${principal.simbolo} ${principal.nombre} (principal)` : 'Principal'}
                </button>
                {secundarias.map((m) => (
                  <button
                    key={m.codigo}
                    onClick={() => set('moneda_precios', m.codigo)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: `1px solid ${cfg.moneda_precios === m.codigo ? '#2563eb' : 'var(--agx-border)'}`,
                      background: cfg.moneda_precios === m.codigo ? '#eff6ff' : 'transparent',
                      color: cfg.moneda_precios === m.codigo ? '#2563eb' : 'var(--agx-text)',
                      fontSize: 12,
                      fontWeight: cfg.moneda_precios === m.codigo ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {m.simbolo} {m.nombre}
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '14px' }}>
                <Toggle
                  checked={!!cfg.mostrar_precio_unidad}
                  onChange={(v) => set('mostrar_precio_unidad', v)}
                  label="Mostrar precio por unidad cuando hay más de una unidad"
                />
                <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '4px' }}>
                  Ej: un producto con 3 unidades mostrará su precio individual (p/u) debajo de la línea.
                </div>
              </div>
            </Section>

            <Section titulo="Moneda del subtotal" defaultOpen>
              <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginBottom: '10px' }}>
                Elige en qué moneda se muestra el subtotal y el total al final del ticket.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { k: 'ambas', l: 'Ambas (REF + Bs)' },
                  { k: 'principal', l: `${principal ? principal.simbolo + ' ' + principal.nombre : 'Principal'}` },
                  ...(secundarias.length ? [{ k: 'secundaria', l: `Solo ${secundarias[0].simbolo} ${secundarias[0].nombre}` }] : []),
                ].map((o) => {
                  const activo = (cfg.subtotal_moneda || 'ambas') === o.k;
                  return (
                    <button
                      key={o.k}
                      onClick={() => set('subtotal_moneda', o.k)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 999,
                        border: `1px solid ${activo ? '#2563eb' : 'var(--agx-border)'}`,
                        background: activo ? '#eff6ff' : 'transparent',
                        color: activo ? '#2563eb' : 'var(--agx-text)',
                        fontSize: 12,
                        fontWeight: activo ? 700 : 500,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </Section>

            <Section titulo={`Notas (${cfg.notas.length})`} defaultOpen>
              {cfg.notas.map((n) => (
                <div key={n.id} style={{ border: '1px solid var(--agx-border)', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                  <textarea
                    style={{ ...S.input, marginBottom: '8px', resize: 'vertical', minHeight: 44 }}
                    value={n.texto}
                    placeholder="Texto de la nota"
                    onChange={(e) => setNota(n.id, { texto: e.target.value })}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <Toggle checked={n.activa} onChange={(v) => setNota(n.id, { activa: v })} label="Activa" />
                    <Toggle checked={!!n.resaltada} onChange={(v) => setNota(n.id, { resaltada: v })} label="Resaltada" />
                    <button onClick={() => eliminarNota(n.id)} style={{ marginLeft: 'auto', ...S.btnSm, color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <FiTrash2 size={13} /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={agregarNota} style={S.btnOutline}>
                <FiPlus size={14} /> Agregar nota
              </button>
            </Section>

            <Section titulo="Orden de bloques">
              <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginBottom: '8px' }}>
                El cliente siempre va debajo del membrete; notas y pie se ordenan abajo.
              </div>
              {cfg.orden.map((bloque, i) => (
                <div key={bloque} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 0', borderBottom: '1px solid var(--agx-border)', fontSize: '13px', color: 'var(--agx-text)' }}>
                  <span style={{ flex: 1 }}>{BLOQUE_LABEL[bloque] || bloque}</span>
                  <button onClick={() => moverBloque(i, -1)} disabled={i === 0} style={{ ...S.btnSm, opacity: i === 0 ? 0.3 : 1, display: 'inline-flex', alignItems: 'center' }}>
                    <FiChevronUp size={14} />
                  </button>
                  <button onClick={() => moverBloque(i, 1)} disabled={i === cfg.orden.length - 1} style={{ ...S.btnSm, opacity: i === cfg.orden.length - 1 ? 0.3 : 1, display: 'inline-flex', alignItems: 'center' }}>
                    <FiChevronDown size={14} />
                  </button>
                </div>
              ))}
            </Section>
          </div>

          {/* ── Vista previa ── */}
          <div className="tickets-preview-col" style={{ minWidth: 0, position: 'sticky', top: 0 }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {[
                ['entrega', 'Ticket de entrega'],
                ['cobro', 'Ticket de cobro'],
              ].map(([modo, label]) => (
                <button
                  key={modo}
                  onClick={() => setModoPreview(modo)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: `1.5px solid ${modoPreview === modo ? '#2563eb' : 'var(--agx-border)'}`,
                    background: modoPreview === modo ? '#eff6ff' : 'var(--agx-surface)',
                    color: modoPreview === modo ? '#2563eb' : 'var(--agx-muted)',
                    fontSize: 13,
                    fontWeight: modoPreview === modo ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '18px',
                background: 'var(--agx-bg2)',
                border: '1px solid var(--agx-border)',
                borderRadius: '14px',
                overflow: 'auto',
              }}
            >
              <div style={{ background: '#fff', boxShadow: '0 1px 8px rgba(0,0,0,0.12)', borderRadius: 4, padding: 0 }}>
                <TicketCanvas
                  recibo={previewRecibo}
                  modo={modoPreview}
                  monedasSel={[principal?.codigo, ...secundarias.map((m) => m.codigo)].filter(Boolean)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ TAB: MONEDAS (integrado de Tasa.js) ════════════════════ */
function TabMonedas() {
  const [monedas, setMonedas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState({});
  const [showNueva, setShowNueva] = useState(false);
  const [nueva, setNueva] = useState({ nombre: '', codigo: '', simbolo: '', tasa_cambio: 1 });
  const [creando, setCreando] = useState(false);
  const [previewMonto, setPreviewMonto] = useState(100);

  const cargar = () => {
    setCargando(true);
    API.get('/ventas/monedas')
      .then((r) => {
        if (r.data.status === 'success') setMonedas(r.data.data || []);
      })
      .catch((e) => console.error('Error cargando monedas:', e?.message || e))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
  }, []);

  const principal = monedas.find((m) => m.es_principal);
  const secundarias = monedas.filter((m) => !m.es_principal);

  const guardarTasa = async (moneda) => {
    setGuardando((prev) => ({ ...prev, [moneda.codigo]: true }));
    try {
      await API.put(`/ventas/monedas/${moneda.id}`, { tasa_cambio: moneda.tasa });
      const ves = monedas.find((m) => ['VES', 'VEF', 'BS'].includes(m.codigo?.toUpperCase()));
      const cop = monedas.find((m) => m.codigo?.toUpperCase() === 'COP');
      await API.put('/pedidos/config/tasa', {
        tasa: ves ? ves.tasa : undefined,
        tasa_pesos: cop ? cop.tasa : undefined,
      });
    } catch (e) {
      alert(
        'Error al guardar: ' +
          (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.message)
      );
    } finally {
      setGuardando((prev) => ({ ...prev, [moneda.codigo]: false }));
    }
  };

  const actualizarTasa = (codigo, valor) => {
    setMonedas((prev) =>
      prev.map((m) => (m.codigo === codigo ? { ...m, tasa: parseFloat(valor) || 0 } : m))
    );
  };

  const guardarTodas = async () => {
    for (const m of secundarias) {
      await guardarTasa(m);
    }
    alert('Tasas guardadas');
  };

  const crearMoneda = async () => {
    if (!nueva.nombre || !nueva.codigo || !nueva.simbolo) {
      alert('Todos los campos son obligatorios');
      return;
    }
    setCreando(true);
    try {
      await API.post('/ventas/monedas', nueva);
      setShowNueva(false);
      setNueva({ nombre: '', codigo: '', simbolo: '', tasa_cambio: 1 });
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreando(false);
    }
  };

  const desactivarMoneda = async (id, nombre) => {
    if (!window.confirm(`Desactivar ${nombre}?`)) return;
    try {
      const r = await API.delete(`/ventas/monedas/${id}`);
      if (r.data.status === 'error') {
        alert(r.data.message);
        return;
      }
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (cargando)
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--agx-faint)' }}>
        Cargando monedas...
      </div>
    );

  return (
    <div>
      <div style={S.card}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <div
              style={{ ...S.label, fontWeight: 700, color: 'var(--agx-muted)', fontSize: '14px' }}
            >
              Monedas y Tasas de Cambio
            </div>
            <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginTop: '2px' }}>
              Configura las monedas aceptadas y sus equivalencias
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={cargar} style={S.btnIcon} title="Recargar">
              <FiRefreshCw size={14} />
            </button>
            <button onClick={() => setShowNueva(true)} style={S.btnPrimary}>
              <FiPlus size={14} /> Agregar
            </button>
          </div>
        </div>

        {principal && (
          <div
            style={{
              background: '#f0fdf4',
              padding: '16px',
              borderRadius: '10px',
              border: '1px solid #bbf7d0',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <FiStar size={14} color="#f59e0b" fill="#f59e0b" />
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#059669' }}>
                Moneda Principal
              </span>
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--agx-text)' }}>
              {principal.simbolo} {principal.nombre} ({principal.codigo})
            </div>
            <div style={{ fontSize: '12px', color: 'var(--agx-muted)', marginTop: '4px' }}>
              Tasa base = 1.0. Todas las demás monedas se calculan respecto a ésta.
            </div>
          </div>
        )}

        {secundarias.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--agx-muted)' }}>
                Tasas de Cambio
              </div>
              <button
                onClick={guardarTodas}
                style={{ ...S.btnPrimary, fontSize: '12px', padding: '8px 14px' }}
              >
                <FiSave size={12} /> Guardar todas
              </button>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '10px',
              }}
            >
              {secundarias.map((m) => (
                <div
                  key={m.codigo}
                  style={{
                    border: '1.5px solid #eef0f4',
                    borderRadius: '12px',
                    padding: '14px',
                    background: 'var(--agx-bg2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--agx-text)' }}>
                        {m.simbolo} {m.nombre}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>{m.codigo}</div>
                    </div>
                    <button
                      onClick={() => desactivarMoneda(m.id, m.nombre)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#ef4444',
                        padding: '4px',
                      }}
                      title="Desactivar"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{ fontSize: '13px', color: 'var(--agx-muted)', whiteSpace: 'nowrap' }}
                    >
                      1 {principal?.codigo || 'USD'} =
                    </span>
                    <input
                      type="number"
                      value={m.tasa}
                      onChange={(e) => actualizarTasa(m.codigo, e.target.value)}
                      style={{
                        ...S.input,
                        flex: 1,
                        textAlign: 'center',
                        fontSize: '16px',
                        fontWeight: 600,
                      }}
                      step="0.01"
                      min="0"
                    />
                    <span style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>{m.codigo}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button
                      onClick={() => guardarTasa(m)}
                      disabled={guardando[m.codigo]}
                      style={S.btnOutline}
                    >
                      {guardando[m.codigo] ? '...' : 'Guardar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {monedas.length > 1 && (
          <div style={{ paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '14px',
                color: 'var(--agx-muted)',
                marginBottom: '12px',
              }}
            >
              Calculadora de Conversion
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}
            >
              <span style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
                Monto en {principal?.codigo || 'USD'}:
              </span>
              <input
                type="number"
                value={previewMonto}
                onChange={(e) => setPreviewMonto(parseFloat(e.target.value) || 0)}
                style={{
                  ...S.input,
                  width: '120px',
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
                step="0.01"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {principal && (
                <div
                  style={{
                    background: '#f0fdf4',
                    borderRadius: '10px',
                    padding: '14px 18px',
                    flex: 1,
                    minWidth: '120px',
                    textAlign: 'center',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669' }}>
                    {principal.simbolo}
                    {fmtMonto(previewMonto)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--agx-muted)', marginTop: '2px' }}>
                    {principal.nombre}
                  </div>
                </div>
              )}
              {secundarias.map((m) => (
                <div
                  key={m.codigo}
                  style={{
                    background: 'var(--agx-bg2)',
                    borderRadius: '10px',
                    padding: '14px 18px',
                    flex: 1,
                    minWidth: '120px',
                    textAlign: 'center',
                    border: '1px solid #eef0f4',
                  }}
                >
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#2952CC' }}>
                    {m.simbolo}
                    {fmtMonto(previewMonto * m.tasa)}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--agx-muted)', marginTop: '2px' }}>
                    {m.nombre}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {monedas.length <= 1 && !cargando && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--agx-faint)' }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--agx-muted)',
                marginBottom: '6px',
              }}
            >
              Solo tienes la moneda principal
            </div>
            <div style={{ fontSize: '12px', marginBottom: '14px' }}>
              Agrega monedas secundarias (Bs, COP, etc.) con sus tasas de cambio
            </div>
            <button onClick={() => setShowNueva(true)} style={S.btnPrimary}>
              <FiPlus size={14} /> Agregar moneda
            </button>
          </div>
        )}
      </div>

      {showNueva && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            style={{
              background: 'var(--agx-surface)',
              borderRadius: '14px',
              padding: '24px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--agx-text)',
                marginBottom: '18px',
              }}
            >
              Nueva moneda
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={S.label}>Nombre</label>
                <input
                  value={nueva.nombre}
                  onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })}
                  placeholder="Ej: Bolivar"
                  style={S.input}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Codigo</label>
                  <input
                    value={nueva.codigo}
                    onChange={(e) => setNueva({ ...nueva, codigo: e.target.value.toUpperCase() })}
                    placeholder="VES"
                    maxLength={10}
                    style={S.input}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Simbolo</label>
                  <input
                    value={nueva.simbolo}
                    onChange={(e) => setNueva({ ...nueva, simbolo: e.target.value })}
                    placeholder="Bs"
                    maxLength={10}
                    style={S.input}
                  />
                </div>
              </div>
              <div>
                <label style={S.label}>Tasa de cambio (1 {principal?.codigo || 'USD'} = ?)</label>
                <input
                  type="number"
                  value={nueva.tasa_cambio}
                  onChange={(e) =>
                    setNueva({ ...nueva, tasa_cambio: parseFloat(e.target.value) || 0 })
                  }
                  style={S.input}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            <div
              style={{ display: 'flex', gap: '8px', marginTop: '18px', justifyContent: 'flex-end' }}
            >
              <button onClick={() => setShowNueva(false)} style={S.btnOutline}>
                Cancelar
              </button>
              <button onClick={crearMoneda} disabled={creando} style={S.btnPrimary}>
                {creando ? 'Creando...' : 'Crear moneda'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ TAB: PAGOS (Métodos de Pago por Sede) ════════════════════ */
function TabPagos({ usuario, tienePermiso, MP_ICONS }) {
  const eid = usuario?.idempresa;
  const [sedes, setSedes] = useState([]);
  const [sedeTab, setSedeTab] = useState(null);
  const [metodosSede, setMetodosSede] = useState([]);
  const [mpCargando, setMpCargando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [guardandoMp, setGuardandoMp] = useState(null);
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const [nuevoForm, setNuevoForm] = useState({
    nombre: '',
    datos_mostrar: [{ etiqueta: '', valor: '' }],
    requiere_comprobante: true,
    mostrar_online: true,
    moneda_codigo: '',
    porcentaje_comision: '',
    recargo_extra: '',
    datos_cliente: [],
  });
  const [planActual, setPlanActual] = useState(null);
  const [planFuturo, setPlanFuturo] = useState(null);

  useEffect(() => {
    if (eid) cargarSedes();
  }, [eid]); // eslint-disable-line
  useEffect(() => {
    if (sedeTab) cargarMetodosSede(sedeTab);
  }, [sedeTab]); // eslint-disable-line
  useEffect(() => {
    if (sedes.length > 0) calcPlan();
  }, [sedes.length]); // eslint-disable-line

  const cargarSedes = async () => {
    try {
      const res = await API.get('/usuarios/sedes');
      if (res.data.status === 'success') {
        setSedes(res.data.data);
        if (!sedeTab && res.data.data.length > 0) setSedeTab(res.data.data[0].codigo);
      }
    } catch (e) {
      console.error('Error sedes:', e);
    }
  };

  const cargarMetodosSede = async (codigo) => {
    if (!eid || !codigo) return;
    setMpCargando(true);
    try {
      const res = await API.get(`/empresas/${eid}/sedes/${codigo}/metodos-pago`);
      if (res.data.status === 'success') setMetodosSede(res.data.data);
    } catch (e) {
      console.error('Error metodos sede:', e);
    } finally {
      setMpCargando(false);
    }
  };

  const calcPlan = () => {
    const planes = [
      { nombre: '1 Sede', precio: 39, max: 1 },
      { nombre: '2 Sedes', precio: 69, max: 2 },
      { nombre: '3 Sedes', precio: 89, max: 3 },
      { nombre: '4 Sedes', precio: 109, max: 4 },
      { nombre: '5 Sedes', precio: 129, max: 5 },
    ];
    const n = sedes.length;
    if (n === 0) {
      setPlanActual(null);
      setPlanFuturo(null);
      return;
    }
    setPlanActual(planes.find((p) => p.max >= n) || planes[planes.length - 1]);
    setPlanFuturo(planes.find((p) => p.max >= n + 1) || null);
  };

  const toggleMetodoSede = async (metodoPagoId, activo) => {
    setMetodosSede((prev) =>
      prev.map((m) => (m.metodo_pago_id === metodoPagoId ? { ...m, activo_sede: activo } : m))
    );
    try {
      await API.post(`/empresas/${eid}/sedes/${sedeTab}/metodos-pago`, {
        metodo_pago_id: metodoPagoId,
        activo,
      });
    } catch (e) {
      setMetodosSede((prev) =>
        prev.map((m) => (m.metodo_pago_id === metodoPagoId ? { ...m, activo_sede: !activo } : m))
      );
      alert('Error al cambiar estado');
    }
  };

  const abrirEditar = (m) => {
    const dn = m.datos_negocio || {};
    const esPagoMovil = m.codigo === 'pago_movil';
    const pmCampos = dn.pago_movil_campos || (esPagoMovil ? ['CI', 'Tlf', 'Banco'] : []);
    setEditandoId(m.metodo_pago_id);
    setEditForm({
      activo: m.activo_sede !== false,
      moneda_codigo: m.moneda_codigo || '',
      datos_mostrar:
        dn.datos_mostrar && dn.datos_mostrar.length
          ? dn.datos_mostrar
          : [{ etiqueta: '', valor: '' }],
      requiere_comprobante: dn.requiere_comprobante !== false,
      mostrar_online: dn.mostrar_online !== false,
      porcentaje_comision: dn.porcentaje_comision || '',
      recargo_extra: dn.recargo_extra || '',
      pago_movil_campos: pmCampos,
      datos_cliente: dn.datos_cliente || [],
    });
  };

  const guardarConfig = async (mpId) => {
    const datos = editForm.datos_mostrar.filter((d) => d.etiqueta.trim() && d.valor.trim());
    const metodoInfo = metodosSede.find((m) => m.metodo_pago_id === mpId);
    const esPaypal = metodoInfo?.codigo === 'paypal';
    const esPagoMovil = metodoInfo?.codigo === 'pago_movil';
    setGuardandoMp(mpId);
    try {
      const dn = { requiere_comprobante: editForm.requiere_comprobante };
      if (editForm.mostrar_online !== null && editForm.mostrar_online !== undefined) {
        dn.mostrar_online = editForm.mostrar_online;
      }
      if (datos.length > 0) dn.datos_mostrar = datos;
      if (esPaypal) {
        dn.porcentaje_comision = parseFloat(editForm.porcentaje_comision) || 0;
        dn.recargo_extra = parseFloat(editForm.recargo_extra) || 0;
      }
      if (esPagoMovil) dn.pago_movil_campos = editForm.pago_movil_campos || [];
      const camposCliente = (editForm.datos_cliente || []).filter((c) => c.nombre.trim());
      if (camposCliente.length > 0) dn.datos_cliente = camposCliente;
      await API.post(`/empresas/${eid}/sedes/${sedeTab}/metodos-pago`, {
        metodo_pago_id: mpId,
        activo: editForm.activo,
        datos_negocio: dn,
        moneda_codigo: editForm.moneda_codigo || undefined,
      });
      setEditandoId(null);
      setMetodosSede((prev) =>
        prev.map((m) =>
          m.metodo_pago_id === mpId
            ? {
                ...m,
                activo_sede: editForm.activo,
                datos_negocio: { ...m.datos_negocio, ...dn },
              }
            : m
        )
      );
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardandoMp(null);
    }
  };

  const addCampo = () =>
    setEditForm((f) => ({
      ...f,
      datos_mostrar: [...f.datos_mostrar, { etiqueta: '', valor: '' }],
    }));
  const removeCampo = (i) =>
    setEditForm((f) => ({ ...f, datos_mostrar: f.datos_mostrar.filter((_, idx) => idx !== i) }));
  const updateCampo = (i, field, val) =>
    setEditForm((f) => {
      const d = [...f.datos_mostrar];
      d[i] = { ...d[i], [field]: val };
      return { ...f, datos_mostrar: d };
    });

  const addNuevoCampo = () =>
    setNuevoForm((f) => ({
      ...f,
      datos_mostrar: [...f.datos_mostrar, { etiqueta: '', valor: '' }],
    }));
  const removeNuevoCampo = (i) =>
    setNuevoForm((f) => ({ ...f, datos_mostrar: f.datos_mostrar.filter((_, idx) => idx !== i) }));
  const updateNuevoCampo = (i, field, val) =>
    setNuevoForm((f) => {
      const d = [...f.datos_mostrar];
      d[i] = { ...d[i], [field]: val };
      return { ...f, datos_mostrar: d };
    });

  const addCampoCliente = (isNew) => {
    const field = { nombre: '', tipo: 'texto', requerido: true };
    if (isNew) setNuevoForm((f) => ({ ...f, datos_cliente: [...f.datos_cliente, field] }));
    else setEditForm((f) => ({ ...f, datos_cliente: [...(f.datos_cliente || []), field] }));
  };
  const removeCampoCliente = (i, isNew) => {
    if (isNew)
      setNuevoForm((f) => ({ ...f, datos_cliente: f.datos_cliente.filter((_, idx) => idx !== i) }));
    else
      setEditForm((f) => ({ ...f, datos_cliente: f.datos_cliente.filter((_, idx) => idx !== i) }));
  };
  const updateCampoCliente = (i, field, val, isNew) => {
    const updater = (f) => {
      const d = [...(f.datos_cliente || [])];
      d[i] = { ...d[i], [field]: val };
      return { ...f, datos_cliente: d };
    };
    if (isNew) setNuevoForm(updater);
    else setEditForm(updater);
  };

  const crearMetodo = async () => {
    const nombre = nuevoForm.nombre.trim();
    if (!nombre) return;
    const datos = nuevoForm.datos_mostrar.filter((d) => d.etiqueta.trim() && d.valor.trim());
    setGuardandoMp('nuevo');
    try {
      const dn = { datos_mostrar: datos, requiere_comprobante: nuevoForm.requiere_comprobante };
      if (nuevoForm.mostrar_online !== null && nuevoForm.mostrar_online !== undefined) {
        dn.mostrar_online = nuevoForm.mostrar_online;
      }
      if (nuevoForm.porcentaje_comision)
        dn.porcentaje_comision = parseFloat(nuevoForm.porcentaje_comision) || 0;
      if (nuevoForm.recargo_extra) dn.recargo_extra = parseFloat(nuevoForm.recargo_extra) || 0;
      const camposCliente = (nuevoForm.datos_cliente || []).filter((c) => c.nombre.trim());
      if (camposCliente.length > 0) dn.datos_cliente = camposCliente;
      const res = await API.post(`/empresas/${eid}/metodos-pago`, {
        nombre,
        datos_mostrar: datos,
        requiere_comprobante: nuevoForm.requiere_comprobante,
        datos_negocio: dn,
        moneda_codigo: nuevoForm.moneda_codigo || undefined,
      });
      if (res.data.status === 'success') {
        setNuevoForm({
          nombre: '',
          datos_mostrar: [{ etiqueta: '', valor: '' }],
          requiere_comprobante: true,
          mostrar_online: true,
          moneda_codigo: '',
          porcentaje_comision: '',
          recargo_extra: '',
          datos_cliente: [],
        });
        setMostrarNuevo(false);
        cargarMetodosSede(sedeTab);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al crear metodo');
    } finally {
      setGuardandoMp(null);
    }
  };

  const sedeSel = sedes.find((s) => s.codigo === sedeTab);

  if (!eid) {
    return (
      <div className="cfg-card" style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--agx-faint)' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>
            No se pudo cargar la empresa
          </div>
          <div style={{ fontSize: '12px' }}>Intenta recargar la página.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cfg-card" style={S.card}>
      <div style={{ marginBottom: '20px' }}>
        <div
          style={{
            ...S.label,
            fontWeight: 700,
            color: 'var(--agx-muted)',
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          Métodos de Pago por Sede
        </div>
        <div style={{ fontSize: '12px', color: 'var(--agx-faint)' }}>
          Activa y configura los metodos de pago disponibles en cada sede
        </div>
      </div>

      {planActual && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            padding: '10px 16px',
            borderRadius: '10px',
            background: 'var(--agx-blue-bg)',
            border: '1px solid var(--agx-border)',
            fontSize: '13px',
            color: 'var(--agx-pri2)',
            fontWeight: 500,
          }}
        >
          Plan: <strong>{planActual.nombre}</strong> · ${planActual.precio}/mes · {sedes.length}{' '}
          sede(s)
          {planFuturo && planFuturo.max !== planActual.max && (
            <span
              style={{
                fontSize: '11px',
                color: 'var(--agx-muted)',
                marginLeft: '8px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: 'var(--agx-surface)',
              }}
            >
              +1 sede → {planFuturo.nombre}
            </span>
          )}
        </div>
      )}

      {/* Tabs de sede */}
      {sedes.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {sedes.map((s) => (
            <button
              key={s.codigo}
              onClick={() => {
                setSedeTab(s.codigo);
                setEditandoId(null);
              }}
              style={{
                padding: '8px 18px',
                borderRadius: '10px',
                border: sedeTab === s.codigo ? '2px solid #2563eb' : '1.5px solid #e5e7eb',
                background: sedeTab === s.codigo ? '#eff6ff' : '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                color: sedeTab === s.codigo ? '#2563eb' : '#4a4d59',
                fontFamily: 'inherit',
                transition: 'all 0.12s',
              }}
            >
              {s.nombre || s.codigo}
            </button>
          ))}
        </div>
      )}

      {sedeSel ? (
        mpCargando ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--agx-faint)',
              fontSize: '13px',
            }}
          >
            Cargando metodos de pago...
          </div>
        ) : (
          <div
            className="cfg-pay-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '10px',
            }}
          >
            {metodosSede.map((m) => {
              const activo = m.activo_sede !== false;
              const expandido = editandoId === m.metodo_pago_id;
              const dn = m.datos_negocio || {};
              const datosMostrar = dn.datos_mostrar || [];

              return (
                <div
                  key={m.metodo_pago_id}
                  style={{
                    borderRadius: '12px',
                    border: activo ? '1.5px solid #d1fae5' : '1.5px solid #f3f4f6',
                    background: activo ? '#fafdfc' : '#fafbfc',
                    padding: expandido ? '16px' : '12px 16px',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '10px',
                        background: activo ? '#ecfdf5' : '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        flexShrink: 0,
                      }}
                    >
                      {MP_ICONS[m.codigo] || '💳'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--agx-text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {m.nombre}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          color: activo ? '#059669' : '#9ca3af',
                          fontWeight: 500,
                        }}
                      >
                        {activo ? 'Activo' : 'Inactivo'}
                      </div>
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}
                    >
                      <button
                        onClick={() => {
                          if (expandido) setEditandoId(null);
                          else abrirEditar(m);
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          background: expandido ? '#e5e7eb' : '#eff6ff',
                          color: expandido ? '#6b7280' : '#2563eb',
                        }}
                      >
                        {expandido ? 'Cancelar' : 'Configurar'}
                      </button>
                      <button
                        onClick={() => toggleMetodoSede(m.metodo_pago_id, !activo)}
                        style={{
                          width: '38px',
                          height: '30px',
                          borderRadius: '999px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          background: activo ? '#059669' : '#d1d5db',
                          color: '#fff',
                          transition: 'background 0.2s',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: 'var(--agx-surface)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            position: 'absolute',
                            top: '5px',
                            transition: 'left 0.2s',
                            left: activo ? '14px' : '4px',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  {expandido && (
                    <div
                      style={{
                        marginTop: '14px',
                        paddingTop: '14px',
                        borderTop: '1.5px solid #f3f4f6',
                      }}
                    >
                      <div style={{ marginBottom: '14px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'var(--agx-muted)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                            }}
                          >
                            Datos bancarios
                          </span>
                          <button onClick={addCampo} style={S.btnSm}>
                            + Agregar
                          </button>
                        </div>
                        {editForm.datos_mostrar.map((d, i) => (
                          <div
                            key={i}
                            className="cfg-edit-fields"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 2fr',
                              gap: '6px',
                              marginBottom: '6px',
                            }}
                          >
                            <input
                              value={d.etiqueta}
                              onChange={(e) => updateCampo(i, 'etiqueta', e.target.value)}
                              style={S.inputSm}
                              placeholder="Etiqueta"
                            />
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <input
                                value={d.valor}
                                onChange={(e) => updateCampo(i, 'valor', e.target.value)}
                                style={S.inputSm}
                                placeholder="Valor"
                              />
                              {editForm.datos_mostrar.length > 1 && (
                                <button
                                  onClick={() => removeCampo(i)}
                                  style={{
                                    ...S.btnSm,
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                    minWidth: '28px',
                                  }}
                                >
                                  X
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {m.codigo === 'paypal' && (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px',
                            marginBottom: '14px',
                          }}
                        >
                          <div>
                            <label
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'var(--agx-muted)',
                                marginBottom: '3px',
                                display: 'block',
                              }}
                            >
                              Comision (%)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={editForm.porcentaje_comision}
                              onChange={(e) =>
                                setEditForm({ ...editForm, porcentaje_comision: e.target.value })
                              }
                              style={S.inputSm}
                              placeholder="5.4"
                            />
                          </div>
                          <div>
                            <label
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'var(--agx-muted)',
                                marginBottom: '3px',
                                display: 'block',
                              }}
                            >
                              Recargo extra ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.recargo_extra}
                              onChange={(e) =>
                                setEditForm({ ...editForm, recargo_extra: e.target.value })
                              }
                              style={S.inputSm}
                              placeholder="0.30"
                            />
                          </div>
                        </div>
                      )}

                      {m.codigo === 'pago_movil' && (
                        <div style={{ marginBottom: '14px' }}>
                          <div
                            style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              color: 'var(--agx-muted)',
                              marginBottom: '8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                            }}
                          >
                            Campos a pedir al cliente
                          </div>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['CI', 'Tlf', 'Banco'].map((campo) => (
                              <label
                                key={campo}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  color: '#4a4d59',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={(editForm.pago_movil_campos || []).includes(campo)}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      pago_movil_campos: e.target.checked
                                        ? [...(prev.pago_movil_campos || []), campo]
                                        : (prev.pago_movil_campos || []).filter((c) => c !== campo),
                                    }))
                                  }
                                  style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                                />
                                {campo === 'CI'
                                  ? 'Cedula / RIF'
                                  : campo === 'Tlf'
                                    ? 'Telefono'
                                    : 'Banco'}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {m.codigo !== 'pago_movil' && (
                        <div style={{ marginBottom: '14px' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '8px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: 'var(--agx-muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                              }}
                            >
                              Datos a pedir al cliente
                            </span>
                            <button onClick={() => addCampoCliente(false)} style={S.btnSm}>
                              + Agregar
                            </button>
                          </div>
                          {(editForm.datos_cliente || []).map((c, i) => (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <input
                                value={c.nombre}
                                onChange={(e) =>
                                  updateCampoCliente(i, 'nombre', e.target.value, false)
                                }
                                style={S.inputSm}
                                placeholder="Nombre del campo"
                              />
                              <label
                                style={{
                                  fontSize: '11px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={c.requerido !== false}
                                  onChange={(e) =>
                                    updateCampoCliente(i, 'requerido', e.target.checked, false)
                                  }
                                  style={{ width: '13px', height: '13px', accentColor: '#2563eb' }}
                                />
                                Req.
                              </label>
                              <button
                                onClick={() => removeCampoCliente(i, false)}
                                style={{
                                  ...S.btnSm,
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  minWidth: '28px',
                                }}
                              >
                                X
                              </button>
                            </div>
                          ))}
                          {(editForm.datos_cliente || []).length === 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>
                              Sin campos. Agrega los datos que necesitas del cliente para verificar
                              el pago.
                            </div>
                          )}
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4d59', display: 'block', marginBottom: 4 }}>
                  Moneda
                </label>
                <select
                  value={editForm.moneda_codigo || ''}
                  onChange={(e) => setEditForm({ ...editForm, moneda_codigo: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
                    fontSize: 13, fontFamily: 'inherit', background: '#fff', outline: 'none' }}>
                  <option value="">-- Seleccionar --</option>
                  <option value="USD">USD ($)</option>
                  <option value="VES">VES (Bs)</option>
                  <option value="COP">COP</option>
                </select>
              </div>

              <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginBottom: '12px',
                          color: '#4a4d59',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.requiere_comprobante !== false}
                          onChange={(e) =>
                            setEditForm({ ...editForm, requiere_comprobante: e.target.checked })
                          }
                          style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                        />
                        Requiere comprobante de pago
                      </label>
                      <label
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer',
                          fontSize: 13, color: '#4a4d59',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editForm.mostrar_online !== false}
                          onChange={(e) =>
                            setEditForm({ ...editForm, mostrar_online: e.target.checked })
                          }
                          style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                        />
                        Mostrar en pedidos online
                      </label>
                      <button
                        onClick={() => guardarConfig(m.metodo_pago_id)}
                        disabled={guardandoMp === m.metodo_pago_id}
                        style={{
                          ...S.btnPrimary,
                          width: '100%',
                          justifyContent: 'center',
                          opacity: guardandoMp === m.metodo_pago_id ? 0.6 : 1,
                        }}
                      >
                        {guardandoMp === m.metodo_pago_id
                          ? 'Guardando...'
                          : 'Guardar configuracion'}
                      </button>
                    </div>
                  )}

                  {activo && !expandido && datosMostrar.length > 0 && (
                    <div
                      style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}
                    >
                      {datosMostrar.map((d, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: '10px',
                            background: 'var(--agx-skeleton)',
                            color: 'var(--agx-muted)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                          }}
                        >
                          {d.etiqueta}: {d.valor}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--agx-faint)' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>
            No tienes sedes configuradas
          </div>
          <div style={{ fontSize: '12px' }}>
            Crea una sede para comenzar a configurar metodos de pago.
          </div>
        </div>
      )}

      {sedeSel && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1.5px solid #f3f4f6' }}>
          {!mostrarNuevo ? (
            <button
              onClick={() => setMostrarNuevo(true)}
              style={{ ...S.btnOutline, background: '#111827', color: '#fff', border: 'none' }}
            >
              + Nuevo método personalizado
            </button>
          ) : (
            <div
              style={{
                borderRadius: '12px',
                border: '1.5px solid #e5e7eb',
                background: 'var(--agx-bg2)',
                padding: '16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '14px',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--agx-text)' }}>
                  Nuevo método de pago
                </span>
                <button
                  onClick={() => setMostrarNuevo(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--agx-faint)',
                    fontSize: '18px',
                  }}
                >
                  X
                </button>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ ...S.label, fontSize: '11px' }}>Nombre del método *</label>
                <input
                  value={nuevoForm.nombre}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, nombre: e.target.value })}
                  style={S.input}
                  placeholder="Ej: Crediabby, Reserve, Cash App..."
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--agx-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Datos bancarios
                  </span>
                  <button onClick={addNuevoCampo} style={S.btnSm}>
                    + Agregar
                  </button>
                </div>
                {nuevoForm.datos_mostrar.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 2fr',
                      gap: '6px',
                      marginBottom: '6px',
                    }}
                  >
                    <input
                      value={d.etiqueta}
                      onChange={(e) => updateNuevoCampo(i, 'etiqueta', e.target.value)}
                      style={S.inputSm}
                      placeholder="Etiqueta"
                    />
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        value={d.valor}
                        onChange={(e) => updateNuevoCampo(i, 'valor', e.target.value)}
                        style={S.inputSm}
                        placeholder="Valor"
                      />
                      {nuevoForm.datos_mostrar.length > 1 && (
                        <button
                          onClick={() => removeNuevoCampo(i)}
                          style={{
                            ...S.btnSm,
                            background: '#fef2f2',
                            color: '#dc2626',
                            minWidth: '28px',
                          }}
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '14px',
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--agx-muted)',
                      marginBottom: '3px',
                      display: 'block',
                    }}
                  >
                    Comision (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={nuevoForm.porcentaje_comision}
                    onChange={(e) =>
                      setNuevoForm({ ...nuevoForm, porcentaje_comision: e.target.value })
                    }
                    style={S.inputSm}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--agx-muted)',
                      marginBottom: '3px',
                      display: 'block',
                    }}
                  >
                    Recargo extra ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={nuevoForm.recargo_extra}
                    onChange={(e) => setNuevoForm({ ...nuevoForm, recargo_extra: e.target.value })}
                    style={S.inputSm}
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--agx-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Datos a pedir al cliente
                  </span>
                  <button onClick={() => addCampoCliente(true)} style={S.btnSm}>
                    + Agregar
                  </button>
                </div>
                {(nuevoForm.datos_cliente || []).map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '4px',
                      alignItems: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    <input
                      value={c.nombre}
                      onChange={(e) => updateCampoCliente(i, 'nombre', e.target.value, true)}
                      style={S.inputSm}
                      placeholder="Nombre del campo"
                    />
                    <label
                      style={{
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={c.requerido !== false}
                        onChange={(e) => updateCampoCliente(i, 'requerido', e.target.checked, true)}
                        style={{ width: '13px', height: '13px', accentColor: '#2563eb' }}
                      />
                      Req.
                    </label>
                    <button
                      onClick={() => removeCampoCliente(i, true)}
                      style={{
                        ...S.btnSm,
                        background: '#fef2f2',
                        color: '#dc2626',
                        minWidth: '28px',
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}
                {(nuevoForm.datos_cliente || []).length === 0 && (
                  <div style={{ fontSize: '11px', color: 'var(--agx-faint)' }}>
                    Sin campos. Agrega los datos que necesitas del cliente para verificar el pago.
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4d59', display: 'block', marginBottom: 4 }}>
                  Moneda
                </label>
                <select
                  value={nuevoForm.moneda_codigo || ''}
                  onChange={(e) => setNuevoForm({ ...nuevoForm, moneda_codigo: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
                    fontSize: 13, fontFamily: 'inherit', background: '#fff', outline: 'none' }}>
                  <option value="">-- Seleccionar --</option>
                  <option value="USD">USD ($)</option>
                  <option value="VES">VES (Bs)</option>
                  <option value="COP">COP</option>
                </select>
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  marginBottom: '14px',
                  color: '#4a4d59',
                }}
              >
                <input
                  type="checkbox"
                  checked={nuevoForm.requiere_comprobante}
                  onChange={(e) =>
                    setNuevoForm({ ...nuevoForm, requiere_comprobante: e.target.checked })
                  }
                  style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                />
                Requiere comprobante de pago
              </label>

              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px',
                  cursor: 'pointer', marginBottom: '14px', color: '#4a4d59',
                }}
              >
                <input
                  type="checkbox"
                  checked={nuevoForm.mostrar_online !== false}
                  onChange={(e) =>
                    setNuevoForm({ ...nuevoForm, mostrar_online: e.target.checked })
                  }
                  style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
                />
                Mostrar en pedidos online
              </label>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setMostrarNuevo(false)} style={S.btnOutline}>
                  Cancelar
                </button>
                <button
                  onClick={crearMetodo}
                  disabled={guardandoMp === 'nuevo'}
                  style={{ ...S.btnPrimary, opacity: guardandoMp === 'nuevo' ? 0.6 : 1 }}
                >
                  {guardandoMp === 'nuevo' ? 'Creando...' : 'Crear método'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════ ESTILOS COMPARTIDOS ════════════════════ */
const S = {
  wrapper: {
    padding: '32px 36px',
    background: 'var(--agx-bg2)',
    minHeight: '100vh',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  card: {
    background: 'var(--agx-surface)',
    borderRadius: '16px',
    padding: '28px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    border: '1px solid var(--agx-border)',
    marginBottom: '20px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--agx-muted)',
    marginBottom: '4px',
    letterSpacing: '0.1px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1.5px solid var(--agx-border)',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--agx-text)',
    fontFamily: 'inherit',
  },
  inputSm: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1.5px solid var(--agx-border)',
    fontSize: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--agx-text)',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    padding: '10px 22px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: '#111827',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  btnOutline: {
    padding: '9px 18px',
    borderRadius: '10px',
    border: '1.5px solid var(--agx-border)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    fontFamily: 'inherit',
  },
  btnSm: {
    padding: '5px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: 'inherit',
    background: 'var(--agx-skeleton)',
    color: 'var(--agx-muted)',
  },
  btnIcon: {
    padding: '8px',
    borderRadius: '8px',
    border: '1.5px solid var(--agx-border)',
    cursor: 'pointer',
    fontSize: '13px',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
  },
};

export default Configuración;
